import React, { useState } from 'react';
import { 
  CalendarPlus, 
  CheckCircle, 
  XCircle, 
  Eye 
} from 'lucide-react';
import { addLeave as dbAddLeave, updateLeave as dbUpdateLeave, deleteLeave as dbDeleteLeave } from '../services/db';

const COLORS = ['#3b5bdb', '#6d28d9', '#2a9d4f', '#d97706', '#c0392b', '#1e7ec8', '#6b4f9e'];
const CBGS = ['#eef2ff', '#f5f3ff', '#edfaf2', '#fffbeb', '#fef2f2', '#e6f3fc', '#f0edfb'];

function ec(i) { return COLORS[i % COLORS.length]; }
function ecb(i) { return CBGS[i % CBGS.length]; }

function Leaves({ leaves, setLeaves, employees, notifications, setNotifications, showToast, openModal }) {
  const [activeTab, setActiveTab] = useState('all');
  const [nextLeaveId, setNextLeaveId] = useState(7);

  const filteredLeaves = leaves.filter((leave) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') {
      return leave.status === 'pending' || leave.status === 'pending_head';
    }
    return leave.status === activeTab;
  });

  const approveLeave = async (id) => {
    const leave = leaves.find(l => l.id === id);
    if (!leave) return;

    try {
      await dbUpdateLeave(id, { status: 'approved' });
      
      const updatedLeaves = leaves.map(l => 
        l.id === id ? { ...l, status: 'approved' } : l
      );
      setLeaves(updatedLeaves);

      const newNotification = {
        id: Date.now(),
        type: 'leave',
        icon: '✅',
        title: 'Leave approved',
        body: `${leave.emp}'s ${leave.type} (${leave.days} days) approved`,
        time: 'Just now',
        read: false
      };
      setNotifications([newNotification, ...notifications]);

      showToast(`${leave.emp}'s leave approved!`, 'ok');
    } catch (error) {
      console.error('Error approving leave:', error);
      showToast('Failed to approve leave', 'err');
    }
  };

  const rejectModal = (id) => {
    const leave = leaves.find(l => l.id === id);
    openModal({
      isOpen: true,
      title: 'Reject Leave',
      content: (
      <>
        <p style={{ fontSize: '13px', color: 'var(--text2)', marginBottom: '12px' }}>
          Rejecting <strong style={{ color: 'var(--text)' }}>{leave.emp}</strong>'s {leave.type} ({leave.days} days)
        </p>
        <div className="fg">
          <label>Rejection reason *</label>
          <textarea id="rr" rows="3" placeholder="Give a clear reason so the employee understands..."></textarea>
        </div>
      </>),
      footer: (
      <>
        <button className="btn btn-g" onClick={() => openModal({ isOpen: false })}>Cancel</button>
        <button className="btn btn-ng" onClick={() => doReject(id)}>✗ Reject & Notify</button>
      </>)
    });
  };

  const doReject = async (id) => {
    const reason = document.getElementById('rr')?.value?.trim();
    if (!reason) {
      showToast('Please provide a reason', 'err');
      return;
    }

    const leave = leaves.find(l => l.id === id);
    
    try {
      await dbUpdateLeave(id, { status: 'rejected' });
      
      const updatedLeaves = leaves.map(l => 
        l.id === id ? { ...l, status: 'rejected' } : l
      );
      setLeaves(updatedLeaves);

      const newNotification = {
        id: Date.now(),
        type: 'leave',
        icon: '❌',
        title: 'Leave rejected',
        body: `${leave.emp}'s leave rejected: ${reason}`,
        time: 'Just now',
        read: false
      };
      setNotifications([newNotification, ...notifications]);

      openModal({ isOpen: false });
      showToast(`${leave.emp}'s leave rejected.`, 'inf');
    } catch (error) {
      console.error('Error rejecting leave:', error);
      showToast('Failed to reject leave', 'err');
    }
  };

  const viewLeave = (id) => {
    const leave = leaves.find(l => l.id === id);
    openModal({
      isOpen: true,
      title: `Leave - ${leave.emp}`,
      content: (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>Type</div>
            <span className="bx bb">{leave.type}</span>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>Status</div>
            <span className={`bx ${leave.status === 'approved' ? 'bg' : leave.status === 'rejected' ? 'br' : 'ba'}`}>
              {leave.status}
            </span>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>From</div>
            <strong>{leave.from}</strong>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>To</div>
            <strong>{leave.to}</strong>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>Duration</div>
            <strong>{leave.days} day{leave.days > 1 ? 's' : ''}</strong>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>Applied on</div>
            {leave.applied}
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>Manager</div>
            {leave.manager} · {leave.mEmail}
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '3px' }}>Reason</div>
            <p style={{ lineHeight: '1.5' }}>{leave.reason}</p>
          </div>
        </div>
      </>),
      footer: <button className="btn btn-g" onClick={() => openModal({ isOpen: false })}>Close</button>
    });
  };

  const openBehalf = () => {
    openModal({
      isOpen: true,
      title: 'Add Leave on Behalf',
      content: (
      <>
        <div className="fg">
          <label>Employee *</label>
          <select id="ble">
            <option disabled selected>Select employeeâ¦</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.name} ({e.dept})</option>
            ))}
          </select>
        </div>
        <div className="fg">
          <label>Leave Type</label>
          <select id="blt">
            <option>Casual Leave</option>
            <option>Sick Leave</option>
            <option>Earned Leave</option>
            <option>Unpaid Leave</option>
          </select>
        </div>
        <div className="fr">
          <div className="fg">
            <label>From *</label>
            <input id="blf" type="date" />
          </div>
          <div className="fg">
            <label>To *</label>
            <input id="blt2" type="date" />
          </div>
        </div>
        <div className="fg">
          <label>Reason</label>
          <textarea id="blr" rows="2" placeholder="Reason for leaveâ¦"></textarea>
        </div>
      </>),
      footer: (
      <>
        <button className="btn btn-g" onClick={() => openModal({ isOpen: false })}>Cancel</button>
        <button className="btn btn-p" onClick={doBehalf}>Submit</button>
      </>)
    });
  };

  const doBehalf = () => {
    const eId = parseInt(document.getElementById('ble').value);
    const emp = employees.find(e => e.id === eId);
    const from = document.getElementById('blf').value;
    const to = document.getElementById('blt2').value;

    if (!emp || !from || !to) {
      showToast('Fill all required fields', 'err');
      return;
    }

    const days = Math.max(1, Math.ceil((new Date(to) - new Date(from)) / (864e5)) + 1);
    const newLeave = {
      id: nextLeaveId,
      eId,
      emp: emp.name,
      ini: emp.ini,
      type: document.getElementById('blt').value,
      from,
      to,
      days,
      reason: document.getElementById('blr').value || 'â',
      status: 'pending',
      applied: new Date().toISOString().split('T')[0],
      manager: emp.manager,
      mEmail: emp.mEmail
    };

    dbAddLeave(newLeave);
    openModal({ isOpen: false });
    showToast(`Leave added for ${emp.name}`, 'ok');
  };

  const pendingCount = leaves.filter((l) => l.status === 'pending' || l.status === 'pending_head').length;

  return (
    <div className="pg">
      <div className="ph">
        <div>
          <h1>Leave Requests</h1>
          <p>{pendingCount} pending approval</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-p" onClick={openBehalf} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CalendarPlus size={16} /> Add (On Behalf)
          </button>
        </div>
      </div>
      <div className="tabs">
        <div className={`tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          All ({leaves.length})
        </div>
        <div className={`tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending ({pendingCount})
        </div>
        <div className={`tab ${activeTab === 'approved' ? 'active' : ''}`} onClick={() => setActiveTab('approved')}>
          Approved
        </div>
        <div className={`tab ${activeTab === 'rejected' ? 'active' : ''}`} onClick={() => setActiveTab('rejected')}>
          Rejected
        </div>
      </div>
      <div className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeaves.map((leave, i) => (
                <tr key={leave.id} className="lr" data-status={leave.status}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="av" style={{ background: ecb(i), color: ec(i), width: '28px', height: '28px', fontSize: '10px' }}>
                        {leave.ini}
                      </div>
                      <span style={{ fontWeight: '600' }}>{leave.emp}</span>
                    </div>
                  </td>
                  <td>
                    <span className="bx bb" style={{ fontSize: '10px' }}>{leave.type}</span>
                  </td>
                  <td style={{ fontSize: '12px' }}>{leave.from}</td>
                  <td style={{ fontSize: '12px' }}>{leave.to}</td>
                  <td style={{ fontWeight: '700' }}>{leave.days}</td>
                  <td style={{ color: 'var(--text2)', fontSize: '12px', maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {leave.reason}
                  </td>
                  <td>
                    <span className={`bx ${leave.status === 'approved' ? 'bg' : leave.status === 'rejected' ? 'br' : 'ba'}`}>
                      {leave.status === 'pending_head' ? 'pending (head)' : leave.status}
                    </span>
                  </td>
                  <td>
                    {leave.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="btn btn-ok btn-sm" onClick={() => approveLeave(leave.id)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <CheckCircle size={14} /> Approve
                        </button>
                        <button className="btn btn-ng btn-sm" onClick={() => rejectModal(leave.id)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <XCircle size={14} /> Reject
                        </button>
                      </div>
                    ) : leave.status === 'pending_head' ? (
                      <span style={{ fontSize: '11px', color: 'var(--text2)', maxWidth: '120px', display: 'inline-block' }}>
                        Awaiting department head
                      </span>
                    ) : (
                      <button className="btn btn-g btn-sm" onClick={() => viewLeave(leave.id)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Eye size={14} /> View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Leaves;
